<template>
  <NavigationLayout
    :navigationTitle="$t('coinHistoryTitle')"
    :navigationBarStyle="{
      border: 0,
      boxShadow: 'none',
      'top': 'var(--safe-area-inset-top, 0px)',
    }"
    :navigationContentStyle="{
      'padding-top': 'calc(44px + var(--safe-area-inset-top, 0px))',
    }"
  >
    <div id="mypage__wrapper" :class="localeClass" slot="contents">
      <div id="mypage__container">
        <div class="coin_info">
          <div class="coin_possessed">
            <div class="coin_text">{{ $t('possessedCoin') }}</div>
            <coin-amount
              :amount="totalCoin"
              :amountStyle="{
                height: '28px',
                lineHeight: '28px',
                fontSize: '20px',
              }"
            />
          </div>
          <div class="coin_breakdown">
            ({{
              $t('coinBreakdown', {
                chargeCoin: formatCoin(chargeCoin),
                freeCoin: formatCoin(freeCoin),
              })
            }})
          </div>
        </div>

        <div class="coin_histories">
          <div
            v-for="(transaction, idx) in transactions"
            :key="idx"
            class="coin_history"
          >
            <p class="history_time">{{ formatTime(transaction.date) }}</p>
            <p class="history_name">{{ transaction.reason }}</p>
            <div
              class="history_amount"
              :class="
                isSpendTransaction(transaction.transactionType)
                  ? 'history_amount_spend'
                  : null
              "
            >
              <p v-if="transaction.balanceChangeCharge !== 0">
                ({{ $t('coinCharge') }})
                {{ formatCoin(transaction.balanceChangeCharge) }}
              </p>
              <p v-if="transaction.balanceChangeFree !== 0">
                ({{ $t('coinFree') }})
                {{ formatCoin(transaction.balanceChangeFree) }}
              </p>
            </div>
          </div>
          <div ref="target"></div>
        </div>
      </div>
    </div>
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import NavigationLayout from '@/components/NavigationLayout.vue'
import ListItem from '@/components/ListItem.vue'
import ListSubheader from '@/components/ListSubheader.vue'
import CoinAmount from '@/components/Mypage/CoinAmount.vue'
import { api } from '@/api/appGatewayServerApi'
import Formatter from '@/utils/Formatter'
import { handleUnauthorized } from '@/utils/handle_error'
import * as models from '@/models'
import { formatTime } from '@/utils/time'
import { transactionTypes, TransactionType } from '@/models'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'

@Component({
  components: {
    NavigationLayout,
    ListItem,
    ListSubheader,
    CoinAmount,
  },
})
export default class MyPage extends Mixins(LanguageQueryHandler) {
  totalCoin: number = 0
  freeCoin: number = 0
  chargeCoin: number = 0
  transactions: models.GamelibTransaction[] = []
  hasNext: boolean = true

  page: number = 0
  processing: boolean = false
  observer!: IntersectionObserver

  $refs!: { [key: string]: Element }

  async created() {
    await this.fetchHistories()

    // 無限スクロール
    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (entry && entry.isIntersecting) {
        this.fetchHistories()
      }
    })
    this.observer.observe(this.$refs.target)
  }

  async fetchHistories() {
    if (this.processing || !this.hasNext) {
      return
    }
    this.processing = true

    try {
      const response = await api().web.mypage.coinHistories({
        page: ++this.page,
      })
      this.totalCoin = response.totalCoin
      this.freeCoin = response.freeCoin
      this.chargeCoin = response.chargeCoin
      this.transactions = [...this.transactions, ...response.transactions]
      this.hasNext = response.hasNext
    } catch (e) {
      handleUnauthorized(e)
    } finally {
      this.processing = false
    }
  }

  formatCoin(amount: number) {
    return Formatter.numberWithCommas(amount)
  }

  formatTime(time: string) {
    return formatTime(time, 'YYYY/MM/DD HH:mm')
  }

  isSpendTransaction(type: TransactionType) {
    return type === transactionTypes.Spend
  }

  get localeClass() {
    return '-' + this.$i18n.locale
  }
}
</script>

<style scoped>
.coin_info {
  width: 100%;
  padding: 8px 16px;
}

.coin_possessed {
  display: flex;
  align-content: center;
  justify-content: space-between;
}

.coin_text {
  font-size: 14px;
  line-height: 20px;
}

.coin_breakdown {
  height: 20px;
  line-height: 20px;
  font-size: 12px;
  color: #808080;
  text-align: right;
  margin: 4px 0 0 0;
}

.coin_history {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 16px;
  border-top: 1px solid #e8e8e8;
}

.history_time {
  width: 100%;
  font-size: 12px;
  line-height: 16px;
  color: #808080;
  font-weight: 400;
  margin: 0 0 3px 0;
}

.history_name {
  display: -webkit-inline-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  width: calc(100% - 108px);
  font-size: 14px;
  font-weight: bold;
  line-height: 20px;
  color: #262626;
}

.history_amount {
  width: 100px;
  display: inline-block;
  font-size: 14px;
  line-height: 140%;
  color: #2b2b2b;
  text-align: right;
  margin: 0 0 0 8px;
}

.history_amount_spend {
  color: #808080;
}

#mypage__wrapper {
  position: relative;
  height: auto;
  min-height: 100%;
}

#mypage__container {
  font-family: sans-serif;
  font-size: 14px;
  line-height: 1.43;
  color: #262626;
  padding-bottom: 40px;
}

#mypage__header {
  padding: 20px 14px;
  margin-top: 0;
  text-align: left;
  background-color: #ffffff;
  line-height: 1.5;
  color: #383838;
}

section {
  margin-top: 20px;
}

.mypage__navigation {
  cursor: pointer;
  pointer-events: auto;
}

.mypage__attentions {
  padding: 0 14px;
  margin: 8px 0 14px;
  font-size: 12px;
  line-height: 150%;
  color: #808080;
}
</style>

<!-- i18n patch styles -->
<style>
  .-ru .history_amount {
    font-size: 10px;
  }
</style>